import { Injectable } from '@angular/core';
import { BaseApi } from './_base-api';
import {
  GovernorateEntity,
  GovernorateCreate,
  GovernorateUpdate,
  AreaCreate,
  AreaUpdate,
  GovernorateItem,
  AreaItem
} from '@models';
import { map, Observable } from 'rxjs';
import { ItemRecord, APIHelper, DataGetFn, DataGetOptions } from '@controladad/ng-base';
import { GetApiAdapter } from '../adapters';

@Injectable({
  providedIn: 'root',
})
export class GovernorateApiService extends BaseApi<GovernorateEntity, GovernorateCreate, GovernorateUpdate> {
  constructor() {
    super('/AdminPanel/Governorate', {
      get: '/AdminPanel/GetGovernorates',
      post: '/AdminPanel/AddGovernorate',
    });
  }

  override getAdapter<GovernorateItem>(options?: DataGetOptions<GovernorateItem> | undefined): DataGetFn<GovernorateItem> {
    return GetApiAdapter<GovernorateItem>('/AdminPanel/GetGovernorates', options);
  }

  // Get single governorate with areas
  getGovernorate(id: number): Observable<GovernorateEntity> {
    return http$.get<GovernorateEntity>(`/AdminPanel/GetGovernorate?Id=${id}`);
  }

  // Create governorate
  override create(model: GovernorateCreate): Observable<any> {
    return http$.post('/AdminPanel/AddGovernorate', {
      governorate: model.fields
    });
  }

  // Update governorate using APIHelper.nestValues pattern
  updateGovernorate(data: GovernorateUpdate): Observable<any> {
    return http$.put('/AdminPanel/EditGovernorate', {
      id: data.governorateId,
      fields: APIHelper.nestValues(data.fields, false)
    });
  }

  // Delete governorates (supports multiple IDs)
  override delete(id: number | number[]): Observable<any> {
    const ids = Array.isArray(id) ? id : [id];
    return http$.delete('/AdminPanel/DeleteGovernorates', {
      body: { ids }
    });
  }

  // Get governorates as item records for dropdowns
  getGovernorateItems(): Observable<ItemRecord<number>[]> {
    return super.getAll<GovernorateItem>(undefined, '/AdminPanel/GetGovernorates').pipe(
      map((governorates) => governorates.map((item) => ({
        label: item.name?.english || `Governorate ${item.id}`,
        value: item.id
      })))
    );
  }

  // Area management methods - fetch fresh data from API
  getAreas(governorateId: number): Observable<AreaItem[]> {
    return this.getGovernorate(governorateId).pipe(
      map((governorate) => governorate.areas || [])
    );
  }

  createArea(model: AreaCreate): Observable<any> {
    return http$.post('/AdminPanel/AddArea', model);
  }

  updateArea(data: AreaUpdate): Observable<any> {
    return http$.put('/AdminPanel/EditArea', {
      areaId: data.areaId,
      fields: APIHelper.nestValues(data.fields, false)
    });
  }

  deleteArea(id: number | number[]): Observable<any> {
    const areaIds = Array.isArray(id) ? id : [id];
    return http$.delete('/AdminPanel/DeleteAreas', {
      body: { areaIds }
    });
  }

  // Get areas as item records for dropdowns
  getAreaItems(governorateId: number): Observable<ItemRecord<number>[]> {
    return this.getAreas(governorateId).pipe(
      map((areas) => areas.map((area) => ({
        label: area.name.english,
        value: area.id
      })))
    );
  }
}
